import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const QuoteFormSection = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1 - Basic Info
    name: '',
    email: '',
    phone: '',
    city: '',
    
    // Step 2 - Project Details
    balconyType: '',
    width: '',
    height: '',
    glazingType: '',
    frameColor: '',
    additionalServices: [],
    
    // Step 3 - Photos & Preferences
    photos: [],
    preferredContact: 'phone',
    preferredTime: '',
    urgency: 'normal',
    budget: '',
    
    // Privacy & Marketing
    gdprConsent: false,
    marketingConsent: false
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const balconyTypes = [
    { id: 'open', name: 'Otvorený balkón', icon: 'Home' },
    { id: 'loggia', name: 'Lodžia', icon: 'Building' },
    { id: 'terrace', name: 'Terasa', icon: 'TreePine' },
    { id: 'winter-garden', name: 'Zimná záhrada', icon: 'Flower' }
  ];

  const glazingTypes = [
    { id: 'single', name: 'Jednokomorové izolačné sklo', price: '45€/m²' },
    { id: 'double', name: 'Dvojkomorové izolačné sklo', price: '65€/m²' },
    { id: 'triple', name: 'Trojkomorové izolačné sklo', price: '85€/m²' }
  ];

  const frameColors = [
    { id: 'white', name: 'Biela', color: '#FFFFFF' },
    { id: 'brown', name: 'Hnedá', color: '#8B4513' },
    { id: 'anthracite', name: 'Antracit', color: '#2F4F4F' },
    { id: 'green', name: 'Zelená', color: '#228B22' }
  ];

  const additionalServices = [
    { id: 'blinds', name: 'Integrované žalúzie', price: '+150€' },
    { id: 'express', name: 'Expresná inštalácia', price: '+100€' },
    { id: 'warranty', name: 'Rozšírená záruka', price: '+80€' },
    { id: 'colors', name: 'Špeciálne farby', price: '+50€' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      additionalServices: prev?.additionalServices?.includes(serviceId)
        ? prev?.additionalServices?.filter(id => id !== serviceId)
        : [...prev?.additionalServices, serviceId]
    }));
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleFileInput = (e) => {
    if (e?.target?.files) {
      handleFiles(e?.target?.files);
    }
  };

  const handleFiles = (files) => {
    const newFiles = Array.from(files)?.slice(0, 5 - uploadedFiles?.length);
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev?.filter((_, i) => i !== index));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData?.name && formData?.email && formData?.phone && formData?.city;
      case 2:
        return formData?.balconyType && formData?.width && formData?.height && formData?.glazingType;
      case 3:
        return formData?.preferredContact && formData?.gdprConsent;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
  };

  const calculateEstimatedPrice = () => {
    if (!formData?.width || !formData?.height || !formData?.glazingType) return 0;
    
    const area = parseFloat(formData?.width) * parseFloat(formData?.height);
    const glazingPrice = glazingTypes?.find(type => type?.id === formData?.glazingType)?.price?.match(/\d+/)?.[0] || 65;
    const basePrice = area * parseInt(glazingPrice);
    
    let additionalCost = 0;
    formData?.additionalServices?.forEach(serviceId => {
      const service = additionalServices?.find(s => s?.id === serviceId);
      if (service) {
        additionalCost += parseInt(service?.price?.match(/\d+/)?.[0] || 0);
      }
    });
    
    return Math.round(basePrice + additionalCost);
  };

  if (submitSuccess) {
    return (
      <section id="quote-form" className="py-20 bg-gradient-to-br from-success/10 to-accent/10">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={40} color="white" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-primary mb-4">
              Ďakujeme za vašu požiadavku!
            </h2>
            <p className="text-lg text-text-secondary mb-8">
              Vaša požiadavka bola úspešne odoslaná. Náš tým vás bude kontaktovať do 2 hodín 
              s presným cenový odhadom a termínom bezplatného zamerania.
            </p>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-border mb-8">
              <h3 className="text-xl font-semibold text-primary mb-4">Čo bude nasledovať?</h3>
              <div className="space-y-4 text-left">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white text-sm font-bold">1</div>
                  <span>Kontaktujeme vás do 2 hodín</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white text-sm font-bold">2</div>
                  <span>Dohodneme si termín bezplatného zamerania</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white text-sm font-bold">3</div>
                  <span>Pripravíme presnú ponuku na mieru</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white text-sm font-bold">4</div>
                  <span>Začneme s realizáciou vášho projektu</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                onClick={() => window.location.href = 'tel:+421905123456'}
                iconName="Phone"
                iconPosition="left"
              >
                Zavolať teraz
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open('https://wa.me/+421905123456?text=Dobrý deň, odoslal som požiadavku cez web.', '_blank')}
                iconName="MessageCircle"
                iconPosition="left"
              >
                WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="quote-form" className="py-20 bg-muted">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-6">
            <Icon name="FileText" size={20} />
            <span className="font-semibold">Bezplatný cenový odhad</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-6">
            Získajte ponuku na mieru
          </h2>
          <p className="text-xl text-text-secondary font-body max-w-3xl mx-auto">
            Vyplňte formulár a získajte presný cenový odhad do 2 hodín. 
            Zameranie a konzultácia sú úplne bezplatné.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-12">
            {[1, 2, 3]?.map((step) => (
              <React.Fragment key={step}>
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-smooth ${
                  currentStep >= step 
                    ? 'bg-accent border-accent text-white' :'bg-white border-border text-text-secondary'
                }`}>
                  {currentStep > step ? (
                    <Icon name="Check" size={20} />
                  ) : (
                    <span className="font-semibold">{step}</span>
                  )}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-2 transition-smooth ${
                    currentStep > step ? 'bg-accent' : 'bg-border'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-border overflow-hidden">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Basic Information */}
              {currentStep === 1 && (
                <div className="p-8">
                  <h3 className="text-2xl font-heading font-semibold text-primary mb-6">
                    Základné informácie
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <Input
                      label="Meno a priezvisko"
                      type="text"
                      placeholder="Zadajte vaše meno"
                      value={formData?.name}
                      onChange={(e) => handleInputChange('name', e?.target?.value)}
                      required
                    />
                    
                    <Input
                      label="Email"
                      type="email"
                      placeholder="vas@email.sk"
                      value={formData?.email}
                      onChange={(e) => handleInputChange('email', e?.target?.value)}
                      required
                    />
                    
                    <Input
                      label="Telefónne číslo"
                      type="tel"
                      placeholder="+421 9XX XXX XXX"
                      value={formData?.phone}
                      onChange={(e) => handleInputChange('phone', e?.target?.value)}
                      required
                    />
                    
                    <Input
                      label="Mesto"
                      type="text"
                      placeholder="Bratislava, Košice, Žilina..."
                      value={formData?.city}
                      onChange={(e) => handleInputChange('city', e?.target?.value)}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Project Details */}
              {currentStep === 2 && (
                <div className="p-8">
                  <h3 className="text-2xl font-heading font-semibold text-primary mb-6">
                    Detaily projektu
                  </h3>
                  
                  {/* Balcony Type */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-text-secondary mb-3">
                      Typ balkóna/terasy
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {balconyTypes?.map((type) => (
                        <button
                          key={type?.id}
                          type="button"
                          onClick={() => handleInputChange('balconyType', type?.id)}
                          className={`p-4 rounded-lg border-2 transition-smooth text-center ${
                            formData?.balconyType === type?.id
                              ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50'
                          }`}
                        >
                          <Icon name={type?.icon} size={24} className="mx-auto mb-2" />
                          <div className="text-sm font-medium">{type?.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Dimensions */}
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <Input
                      label="Šírka (m)"
                      type="number"
                      placeholder="3.5"
                      step="0.1"
                      min="1"
                      max="10"
                      value={formData?.width}
                      onChange={(e) => handleInputChange('width', e?.target?.value)}
                      required
                    />
                    
                    <Input
                      label="Výška (m)"
                      type="number"
                      placeholder="2.5"
                      step="0.1"
                      min="1.5"
                      max="4"
                      value={formData?.height}
                      onChange={(e) => handleInputChange('height', e?.target?.value)}
                      required
                    />
                  </div>

                  {/* Glazing Type */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-text-secondary mb-3">
                      Typ zasklenia
                    </label>
                    <div className="space-y-3">
                      {glazingTypes?.map((type) => (
                        <button
                          key={type?.id}
                          type="button"
                          onClick={() => handleInputChange('glazingType', type?.id)}
                          className={`w-full p-4 rounded-lg border-2 transition-smooth text-left ${
                            formData?.glazingType === type?.id
                              ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{type?.name}</span>
                            <span className="text-accent font-semibold">{type?.price}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Frame Color */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-text-secondary mb-3">
                      Farba rámu
                    </label>
                    <div className="grid grid-cols-4 gap-4">
                      {frameColors?.map((color) => (
                        <button
                          key={color?.id}
                          type="button"
                          onClick={() => handleInputChange('frameColor', color?.id)}
                          className={`p-3 rounded-lg border-2 transition-smooth ${
                            formData?.frameColor === color?.id
                              ? 'border-accent' :'border-border hover:border-accent/50'
                          }`}
                        >
                          <div
                            className="w-8 h-8 rounded-full mx-auto mb-2 border border-gray-300"
                            style={{ backgroundColor: color?.color }}
                          />
                          <div className="text-xs font-medium text-center">{color?.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Additional Services */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-3">
                      Doplnkové služby (voliteľné)
                    </label>
                    <div className="grid md:grid-cols-2 gap-4">
                      {additionalServices?.map((service) => (
                        <div
                          key={service?.id}
                          className={`p-4 rounded-lg border-2 transition-smooth cursor-pointer ${
                            formData?.additionalServices?.includes(service?.id)
                              ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50'
                          }`}
                          onClick={() => handleServiceToggle(service?.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{service?.name}</div>
                              <div className="text-sm text-accent font-semibold">{service?.price}</div>
                            </div>
                            <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                              formData?.additionalServices?.includes(service?.id)
                                ? 'border-accent bg-accent' :'border-border'
                            }`}>
                              {formData?.additionalServices?.includes(service?.id) && (
                                <Icon name="Check" size={16} color="white" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Price Estimate */}
                  {formData?.width && formData?.height && formData?.glazingType && (
                    <div className="mt-6 p-4 bg-accent/10 rounded-lg">
                      <div className="text-center">
                        <div className="text-sm text-white/90 mb-1 ">Orientačná cena:</div>
                        <div className="text-2xl font-bold text-accent">
                          {calculateEstimatedPrice()}€
                        </div>
                        <div className="text-xs text-text-secondary">
                          Konečná cena bude upresnená po zameraní
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Photos & Preferences */}
              {currentStep === 3 && (
                <div className="p-8">
                  <h3 className="text-2xl font-heading font-semibold text-primary mb-6">
                    Fotografie a preferencie
                  </h3>
                  
                  {/* Photo Upload */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-text-secondary mb-3">
                      Fotografie balkóna (voliteľné, max 5 fotografií)
                    </label>
                    <div
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-smooth ${
                        dragActive ? 'border-accent bg-accent/10' : 'border-border'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <Icon name="Upload" size={48} className="mx-auto mb-4 text-text-secondary" />
                      <p className="text-text-secondary mb-4">
                        Pretiahnite fotografie sem alebo kliknite pre výber
                      </p>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileInput}
                        className="hidden"
                        id="file-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('file-upload')?.click()}
                        iconName="Camera"
                        iconPosition="left"
                      >
                        Vybrať fotografie
                      </Button>
                    </div>
                    
                    {/* Uploaded Files */}
                    {uploadedFiles?.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {uploadedFiles?.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <div className="flex items-center space-x-3">
                              <Icon name="Image" size={20} />
                              <span className="text-sm">{file?.name}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-destructive hover:text-destructive/80"
                            >
                              <Icon name="X" size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Contact Preferences */}
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-3">
                        Preferovaný spôsob kontaktu
                      </label>
                      <div className="space-y-2">
                        {[
                          { id: 'phone', name: 'Telefón', icon: 'Phone' },
                          { id: 'email', name: 'Email', icon: 'Mail' },
                          { id: 'whatsapp', name: 'WhatsApp', icon: 'MessageCircle' }
                        ]?.map((method) => (
                          <button
                            key={method?.id}
                            type="button"
                            onClick={() => handleInputChange('preferredContact', method?.id)}
                            className={`w-full p-3 rounded-lg border-2 transition-smooth text-left flex items-center space-x-3 ${
                              formData?.preferredContact === method?.id
                                ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50'
                            }`}
                          >
                            <Icon name={method?.icon} size={20} />
                            <span>{method?.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <Input
                      label="Preferovaný čas kontaktu"
                      type="text"
                      placeholder="napr. 9:00-17:00, večer po 18:00"
                      value={formData?.preferredTime}
                      onChange={(e) => handleInputChange('preferredTime', e?.target?.value)}
                    />
                  </div>

                  {/* GDPR Consent */}
                  <div className="space-y-4">
                    <Checkbox
                      label="Súhlasím so spracovaním osobných údajov"
                      description="Vaše údaje použijeme iba na prípravu ponuky a kontaktovanie. Viac informácií v zásadách ochrany osobných údajov."
                      checked={formData?.gdprConsent}
                      onChange={(e) => handleInputChange('gdprConsent', e?.target?.checked)}
                      required
                    />
                    
                    <Checkbox
                      label="Súhlasím s marketingovými komunikáciami"
                      description="Chcem dostávať informácie o akciách a novinkách (voliteľné)"
                      checked={formData?.marketingConsent}
                      onChange={(e) => handleInputChange('marketingConsent', e?.target?.checked)}
                    />
                  </div>
                </div>
              )}

              {/* Form Navigation */}
              <div className="px-8 py-6 bg-muted border-t border-border flex justify-between items-center">
                <div>
                  {currentStep > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={prevStep}
                      iconName="ArrowLeft"
                      iconPosition="left"
                    >
                      Späť
                    </Button>
                  )}
                </div>

                <div className="text-sm text-text-secondary">
                  Krok {currentStep} z 3
                </div>

                <div>
                  {currentStep < 3 ? (
                    <Button
                      type="button"
                      variant="default"
                      onClick={nextStep}
                      disabled={!validateStep(currentStep)}
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      Pokračovať
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      variant="default"
                      disabled={!validateStep(3) || isSubmitting}
                      loading={isSubmitting}
                      iconName="Send"
                      iconPosition="right"
                    >
                      {isSubmitting ? 'Odosielam...' : 'Odoslať požiadavku'}
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteFormSection;